import React from "react";
import Card from "@imminently/decisively-ui/Card";
import Avatar from "@imminently/decisively-ui/Avatar";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import Icon from "@imminently/decisively-ui/Icon";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { CardMenu, MenuSlot, UserStack, type CardAction, type CardUser } from "./_shared";

export interface WorkspaceCardProps {
  name: string;
  /** Fallback text for Avatar when name is not a string */
  alt?: string;
  description?: string;
  visibility?: "public" | "private";
  tags?: string[];
  /** Marks a workspace whose projects run as canary tests */
  canaryTestingWorkspace?: boolean;
  users?: CardUser[];
  menu?: React.ReactNode;
  action?: CardAction;
}

/** Bird chip marking a Canary Testing workspace */
export const CanaryTestingWorkspaceChip = () => {
  const { t } = useTranslation();
  const label = t("workspaces.canary_testing_workspace");
  return (
    <Chip
      variant="subtle"
      size="sm"
      icon={<Icon name="bird" />}
      title={label}
      aria-label={label}
    />
  );
};

const VISIBILITY_VARIANT = {
  public: "success",
  private: "info",
} as const;

export const WorkspaceCard = ({
  name,
  description,
  visibility,
  tags,
  canaryTestingWorkspace,
  users,
  menu,
  action,
}: WorkspaceCardProps) => {
  const { t } = useTranslation();
  const hasFooter = (users && users.length > 0) || Boolean(action);
  const visibilityLabel = visibility
    ? t(`projects.visibility_${visibility}`)
    : t("projects.visibility_public");
  const visibilityVariant = VISIBILITY_VARIANT[visibility ?? "public"];

  return (
    <Card className="p-4 rounded-lg gap-3 group/card hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Avatar name={name} size="lg" className="shrink-0 rounded-md" />
        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
          <span className="font-semibold text-xl leading-tight truncate">{name}</span>
          {description && (
            <span className="text-muted-foreground text-xs font-medium truncate">{description}</span>
          )}
        </div>
        {menu && (
          <MenuSlot>
            <CardMenu>{menu}</CardMenu>
          </MenuSlot>
        )}
      </div>

      {/* Visibility & tags */}
      <div className="flex items-center gap-1.5 flex-wrap min-h-5">
        <Chip variant={visibilityVariant} size="sm" label={<span className="capitalize">{visibilityLabel}</span>} />
        {tags?.map(tag => (
          <Chip key={tag} variant="subtle" size="sm" label={tag} />
        ))}
        {canaryTestingWorkspace ? <CanaryTestingWorkspaceChip /> : null}
      </div>

      {/* Footer */}
      {hasFooter && (
        <div className="flex items-center gap-2 pt-3">
          {users && <UserStack users={users} />}
          {action && (
            <Button label={action.name}
              className={cn("shrink-0", !users?.length && "ml-auto")}
              style={users?.length ? { marginLeft: "auto" } : undefined}
              size="sm"
              variant="outline"
              intent="secondary"
              onClick={action.onClick}
            />
          )}
        </div>
      )}
    </Card>
  );
};
